import { useState, useEffect } from "react";

import Box from "@mui/material/Box";
import BaseConfigModal from "@plannel/components/modal/BaseConfigModal";
import Autocomplete from '@mui/material/Autocomplete';
import TextField from "@mui/material/TextField";
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import Divider from '@mui/material/Divider';
import Checkbox from '@mui/material/Checkbox';
import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import { grey } from '@mui/material/colors';

import isEmail from 'validator/lib/isEmail';
import { isEmpty, isEqual } from "lodash/lang";
import { withTranslation } from "react-i18next";

import GridUtils from "@plannel/components/aggrid/GridUtils";
import { sortBy } from "lodash";

const UserModal = (props) => {
  const { t, open, close, postn, sites, roles, oldSelectedUser, selectedUser, setSelectedUser, newUser, setDialogInfo } = props;
  const SELECT_ALL = "Select All";
  const DESELECT_ALL = "Deselect All";
  const [showPassword, setShowPassword] = useState(false);

  const [usernameError, setUsernameError] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [expanded, setExpanded] = useState("");

  const userPostnIds = (Array.isArray(selectedUser?.postn) ? selectedUser.postn : []).map(position => position.id);
  const postnOptions = postn?.filter(position => position.activeFlg || userPostnIds.includes(position.id)) ?? [];
  const duplicatePostnNames = (postn ?? []).map(position => position.name).filter((name, index, names) => names.indexOf(name) !== index);
  const getPostnLabel = position => duplicatePostnNames.includes(position.name) ? `${position.name} (${position.postnCd})` : position.name;
  const getPostnOptionLabel = option => {
    const position = postnOptions.find(element => element.id === option);
    return position ? getPostnLabel(position) : option;
  };
  const postnIdList = sortBy(postnOptions, getPostnLabel).map(position => position.id);
  const selectedPostnIds = postnIdList.filter(id => userPostnIds.includes(id));
  const siteNmList = sites?.reduce((acc, cur) => [...acc, cur.name], []);
  const allSitesSelected = siteNmList.length > 0 && selectedUser?.siteNm?.length === siteNmList.length;
  const allPostnSelected = postnIdList.length > 0 && selectedPostnIds.length === postnIdList.length;
  const allRolesSelected = roles?.length > 0 && selectedUser?.roles?.length === roles?.length;
  const dynamicSiteOptions = allSitesSelected ? [DESELECT_ALL, ...sortBy(siteNmList)] : [SELECT_ALL, ...sortBy(siteNmList)];
  const dynamicPostnOptions = allPostnSelected ? [DESELECT_ALL, ...postnIdList] : [SELECT_ALL, ...postnIdList];
  const dynamicRolesOptions = allRolesSelected ? [{ name: DESELECT_ALL }, ...roles] : [{ name: SELECT_ALL }, ...roles];

  const handleSelectedUser = e => {
    setSelectedUser({
      ...selectedUser,
      [e.target.name]: e.target.value
    });
  }

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const handleMouseDownPassword = (event) => {
    event.preventDefault();
  };

  const onChangeUsername = e => {
    const newUsername = e.target.value;
    handleSelectedUser(e);

    if (newUser) {
      if (isEmpty(newUsername)) {
        setUsernameError(t("MSG_FieldRequired"));
      } else if (!isEmail(newUsername)) {
        setUsernameError(t("MSG_CheckUsername"));
      } else {
        setUsernameError("");
      }
    } else {
      setUsernameError("");
    }
  };

  const onChangePassword = e => {
    const newPassword = e.target.value;
    handleSelectedUser(e);

    if (isEmpty(newPassword)) {
      if (newUser) {
        setPasswordError(t("MSG_FieldRequired"));
      } else {
        setPasswordError("");
      }
    } else {
      setPasswordError("");
    }
  };

  const handleSelectedUserCheckbox = e => {
    const { name, checked } = e.target;
    if (checked && isEqual(name, "activeFlg")) {
      if (!newUser) {
        setDialogInfo({ open: true, actionName: "ACTIVE_CONFIRM", title: "confirm", content: t("MSG_ActiveConfirm") });
      } else {
        setSelectedUser({
          ...selectedUser,
          delFlg: false,
          [name]: checked
        });
      }
    } else if (checked && isEqual(name, "delFlg")) {
      setSelectedUser({
        ...selectedUser,
        activeFlg: false,
        [name]: checked
      });
    } else {
      setSelectedUser({
        ...selectedUser,
        [name]: checked
      });
    }
  }

  const handleSelectedPosition = newValue => {
    const postnList = [];
    for (const position of postnOptions) {
      if (newValue.includes(position.id)) {
        postnList.push(position);
      }
    }

    setSelectedUser({
      ...selectedUser,
      postn: postnList,
      postnNm: postnList.map(position => position.name),
    });
  }

  const handleSelectedSite = newValue => {
    const siteList = [];
    for (const site of sites) {
      if (newValue.includes(site.name)) {
        siteList.push(site);
      }
    }

    setSelectedUser({
      ...selectedUser,
      site: siteList,
      siteNm: newValue,
    });
  }

  const handleSelectedUserRoles = newValue => {
    setSelectedUser({
      ...selectedUser,
      roles: typeof newValue === 'string' ? newValue.split(',') : newValue,
    });
  }

  const handleClose = () => {
    if (isEqual(selectedUser, oldSelectedUser)) {
      close(false);
    } else {
      setDialogInfo({ open: true, title: "confirm", content: `${t("MSG_NotSavedAfterChanged")}\n${t("MSG_Continue")}` });
    }
  }

  const getUserRoleLabel = option => {
    if (option.name === SELECT_ALL || option.name === DESELECT_ALL) {
      return option.name;
    }
    return GridUtils.gridValueL10N(option.name, { headerName: "userRoles" });
  }

  useEffect(() => {
    if (open) {
      setUsernameError("");
      setPasswordError("");
    }
    setExpanded("");

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  return (
    /* 권한 패널을 펼치면 폭이 늘어난다 — 프리셋으로는 표현이 안 돼 width 를 직접 준다 */
    <BaseConfigModal
      open={open}
      onClose={handleClose}
      title={newUser ? t("newUser") : t("editUser")}
      onSave={() => setDialogInfo({ open: true, title: "save", content: t("MSG_SaveConfirm") })}
      saveDisabled={Boolean(usernameError) || Boolean(passwordError)}
      width={(expanded ? 750 : 450) + 32}
      paperSx={{ transition: "width 0.3s ease" }}>
      <Box id={"user-modal"}>
        <Box sx={{ display: "flex", height: 520 }}>
          <Box sx={{ display: "flex", flexDirection: "column", flex: 1, height: '100%', pr: expanded ? 1 : 0 }}>
            <Box sx={{ flex: 1, overflow: "auto", pr: 1 }}>
              <Grid item xs={12} sx={{ mt: 2, minWidth: "100px", background: newUser ? "none" : grey[100] }}>
                <TextField
                  fullWidth
                  id="username"
                  name="username"
                  size="small"
                  type="email"
                  autoComplete="off"
                  label={t("emailAddress")}
                  value={selectedUser?.username}
                  onChange={onChangeUsername}
                  error={Boolean(usernameError)}
                  helperText={usernameError}
                  required={newUser}
                  disabled={!newUser}
                />
              </Grid>
              <Grid item xs={12} sx={{ my: 2, minWidth: "100px" }}>
                <TextField
                  fullWidth
                  id="password"
                  name="password"
                  size="small"
                  autoComplete="new-password"
                  type={showPassword ? 'text' : 'password'}
                  label={t("newPassword")}
                  value={selectedUser?.password}
                  InputProps={{
                    endAdornment:
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle password visibility"
                          onClick={handleClickShowPassword}
                          onMouseDown={handleMouseDownPassword}
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                  }}
                  onChange={onChangePassword}
                  error={Boolean(passwordError)}
                  helperText={passwordError}
                  required={newUser}
                />
              </Grid>
              <Grid container sx={{ minWidth: "100px", spacing: 1, mt: 2 }}>
                <Grid item xs={5.6} sx={{ mr: 1.3 }}>
                  <TextField
                    fullWidth
                    id="lastName"
                    name="lastName"
                    size="small"
                    label={t("lastName")}
                    value={selectedUser.lastName}
                    onChange={handleSelectedUser}
                  />
                </Grid>
                <Grid item xs={5.6} sx={{ ml: 1.3 }}>
                  <TextField
                    fullWidth
                    id="firstName"
                    name="firstName"
                    size="small"
                    label={t("firstName")}
                    value={selectedUser.firstName}
                    onChange={handleSelectedUser}
                  />
                </Grid>
              </Grid>
              <Grid item xs={12} sx={{ my: 2, minWidth: "100px" }}>
                <TextField
                  fullWidth
                  id="dispName"
                  name="dispName"
                  size="small"
                  label={t("displayName")}
                  value={selectedUser?.dispName}
                  onChange={handleSelectedUser}
                />
              </Grid>
              <Grid item xs={12} sx={{ my: 2, minWidth: "100px" }}>
                <TextField
                  fullWidth
                  id="deptName"
                  name="deptName"
                  size="small"
                  label={t("deptName")}
                  value={selectedUser?.deptName}
                  onChange={handleSelectedUser}
                />
              </Grid>
              <Grid item xs={12} sx={{ mt: 2, minWidth: "100px" }}>
                <Autocomplete
                  multiple
                  options={dynamicRolesOptions}
                  getOptionLabel={getUserRoleLabel}
                  value={selectedUser?.roles || []}
                  disableCloseOnSelect
                  size="small"
                  isOptionEqualToValue={(option, value) => option.name === value.name}
                  onOpen={() => setExpanded("role")}
                  open={false}
                  onChange={(_, newValue) => handleSelectedUserRoles(newValue)}
                  renderInput={(params) => (
                    <TextField {...params} label={t("role")} />
                  )}
                />
              </Grid>
              <Grid item xs={12} sx={{ mt: 2, minWidth: "100px" }}>
                <Autocomplete
                  multiple
                  options={dynamicPostnOptions}
                  value={selectedPostnIds}
                  getOptionLabel={getPostnOptionLabel}
                  disableCloseOnSelect
                  size="small"
                  onOpen={() => setExpanded("postn")}
                  open={false}
                  onChange={(_, newValue) => handleSelectedPosition(newValue)}
                  renderInput={(params) => (
                    <TextField {...params} label={t("postn")} />
                  )}
                />
              </Grid>
              <Grid item xs={12} sx={{ mt: 2, minWidth: "100px" }}>
                <Autocomplete
                  multiple
                  options={dynamicSiteOptions}
                  value={selectedUser?.siteNm || []}
                  disableCloseOnSelect
                  size="small"
                  onOpen={() => setExpanded("site")}
                  open={false}
                  onChange={(_, newValue) => {
                    handleSelectedSite(newValue);
                  }}
                  renderInput={(params) => (
                    <TextField {...params} label={t("site")} />
                  )}
                />
              </Grid>
              <Grid container sx={{ minWidth: "100px", mt: 2 }}>
                <Box component={"fieldset"} sx={{ borderColor: "rgba(0, 0, 0, 0.23)", borderWidth: "1px", borderStyle: "solid", borderRadius: "2%", width: "inherit", p: 0 }}>
                  <FormControl sx={{ width: "50%" }}>
                    <FormControlLabel
                      control={<Checkbox name="activeFlg" size="small" checked={Boolean(selectedUser?.activeFlg)} onChange={handleSelectedUserCheckbox} />}
                      label={t("activeFlg")}
                      labelPlacement="start"
                      sx={{ ml: 1, alignSelf: "center" }}
                    />
                  </FormControl>
                  <FormControl sx={{ width: "50%" }}>
                    <FormControlLabel
                      control={<Checkbox name="delFlg" size="small" checked={Boolean(selectedUser?.delFlg)} onChange={handleSelectedUserCheckbox} />}
                      label={t("delFlg")}
                      labelPlacement="start"
                      sx={{ ml: 0, alignSelf: "center" }}
                    />
                  </FormControl>
                </Box>
              </Grid>
            </Box>
          </Box>
          {expanded && (
            <>
              <Box sx={{ display: "flex", flexDirection: "column", width: 300, height: 'calc(100% - 15px)', pl: 1 }}>
                <Box sx={{ display: "flex", flexDirection: "row", alignItems: "center", justifyContent: "space-between" }}>
                  <Typography sx={{ height: "5%", mb: 1 }}>
                    {expanded === 'role' && t("role")}
                    {expanded === 'postn' && t("postn")}
                    {expanded === 'site' && t("site")}
                  </Typography>
                  <IconButton variant="outlined" onClick={() => setExpanded("")}>
                    <ChevronLeftIcon />
                  </IconButton>
                </Box>
                {expanded === 'role' && (
                  <Box sx={{ pt: 1, overflow: "auto" }}>
                    {dynamicRolesOptions.map((option) => (
                      <>
                        <Box key={option.name} sx={{ display: "flex", alignItems: "center" }}>
                          {
                            <Checkbox
                              disabled={roles.length <= 0}
                              checked={Boolean(
                                (option.name === SELECT_ALL || option.name === DESELECT_ALL) ?
                                  allRolesSelected : selectedUser?.roles?.some(r => r.name === option.name)
                              )}
                              size="small"
                              onChange={() => {
                                if (option.name === SELECT_ALL) {
                                  handleSelectedUserRoles(dynamicRolesOptions.slice(1));
                                }
                                else if (option.name === DESELECT_ALL) {
                                  handleSelectedUserRoles([]);
                                } else {
                                  let newValue = Array.isArray(selectedUser?.roles) ? [...selectedUser.roles] : [];
                                  if (newValue.some(r => r.name === option.name)) {
                                    newValue = newValue.filter(r => r.name !== option.name);
                                  } else {
                                    newValue.push(option);
                                  }
                                  handleSelectedUserRoles(newValue);
                                }
                              }}
                            />
                          }
                          <Box sx={{
                            flex: 1, cursor: roles.length <= 0 ? "default" : "pointer",
                            color: roles.length <= 0 ? "text.disabled" : "inherit"
                          }}
                            onClick={() => {
                              if (roles.length <= 0) return;
                              if (option.name === SELECT_ALL) {
                                handleSelectedUserRoles(dynamicRolesOptions.slice(1));
                              } else if (option.name === DESELECT_ALL) {
                                handleSelectedUserRoles([]);
                              } else {
                                let newValue = Array.isArray(selectedUser?.roles) ? [...selectedUser.roles] : [];
                                if (newValue.some(r => r.name === option.name)) {
                                  newValue = newValue.filter(r => r.name !== option.name);
                                } else {
                                  newValue.push(option);
                                }
                                handleSelectedUserRoles(newValue);
                              }
                            }}
                          >
                            {getUserRoleLabel(option)}
                          </Box>
                        </Box>
                        {(option.name === SELECT_ALL || option.name === DESELECT_ALL) && (
                          <Divider sx={{ mt: 0.5, mb: 0.5, width: "100%" }} />
                        )}
                      </>
                    ))}
                  </Box>
                )}
                {expanded === 'postn' && (
                  <Box sx={{ pt: 1, overflow: "auto" }}>
                    {dynamicPostnOptions.map((option) => (
                      <>
                        <Box key={option} sx={{ display: "flex", alignItems: "center" }}>
                          <Checkbox
                            disabled={postnIdList.length <= 0}
                            checked={Boolean(
                              (option === SELECT_ALL || option === DESELECT_ALL) ?
                                allPostnSelected : selectedPostnIds.includes(option)
                            )}
                            size="small"
                            onChange={() => {
                              if (option === SELECT_ALL) {
                                handleSelectedPosition(dynamicPostnOptions.slice(1));
                              } else if (option === DESELECT_ALL) {
                                handleSelectedPosition([]);
                              } else {
                                let newValue = [...selectedPostnIds];
                                if (newValue.includes(option)) {
                                  newValue = newValue.filter(v => v !== option);
                                } else {
                                  newValue.push(option);
                                }
                                handleSelectedPosition(newValue);
                              }
                            }}
                          />
                          <Box sx={{
                            flex: 1, cursor: postnIdList.length <= 0 ? "default" : "pointer",
                            color: postnIdList.length <= 0 ? "text.disabled" : "inherit"
                          }}
                            onClick={() => {
                              if (postnIdList.length <= 0) return;
                              if (option === SELECT_ALL) {
                                handleSelectedPosition(dynamicPostnOptions.slice(1));
                              } else if (option === DESELECT_ALL) {
                                handleSelectedPosition([]);
                              } else {
                                let newValue = [...selectedPostnIds];
                                if (newValue.includes(option)) {
                                  newValue = newValue.filter(v => v !== option);
                                } else {
                                  newValue.push(option);
                                }
                                handleSelectedPosition(newValue);
                              }
                            }}
                          >
                            {getPostnOptionLabel(option)}
                          </Box>
                        </Box>
                        {(option === SELECT_ALL || option === DESELECT_ALL) && (
                          <Divider sx={{ mt: 0.5, mb: 0.5, width: "100%" }} />
                        )}
                      </>
                    ))}
                  </Box>
                )}
                {expanded === 'site' && (
                  <Box sx={{ pt: 1, overflow: "auto" }}>
                    {dynamicSiteOptions.map((option) => (
                      <>
                        <Box key={option} sx={{ display: "flex", alignItems: "center" }}>
                          <Checkbox
                            disabled={siteNmList.length <= 0}
                            checked={Boolean(
                              (option === SELECT_ALL || option === DESELECT_ALL) ?
                                allSitesSelected : selectedUser?.siteNm?.includes(option)
                            )}
                            size="small"
                            onChange={() => {
                              if (option === SELECT_ALL) {
                                handleSelectedSite(dynamicSiteOptions.slice(1));
                              } else if (option === DESELECT_ALL) {
                                handleSelectedSite([]);
                              } else {
                                let newValue = Array.isArray(selectedUser?.siteNm) ? [...selectedUser.siteNm] : [];
                                if (newValue.includes(option)) {
                                  newValue = newValue.filter(v => v !== option);
                                } else {
                                  newValue.push(option);
                                }
                                handleSelectedSite(newValue);
                              }
                            }}
                          />
                          <Box sx={{
                            flex: 1, cursor: siteNmList.length <= 0 ? "default" : "pointer",
                            color: siteNmList.length <= 0 ? "text.disabled" : "inherit"
                          }}
                            onClick={() => {
                              if (siteNmList.length <= 0) return;
                              if (option === SELECT_ALL) {
                                handleSelectedSite(dynamicSiteOptions.slice(1));
                              } else if (option === DESELECT_ALL) {
                                handleSelectedSite([]);
                              } else {
                                let newValue = Array.isArray(selectedUser?.siteNm) ? [...selectedUser.siteNm] : [];
                                if (newValue.includes(option)) {
                                  newValue = newValue.filter(v => v !== option);
                                } else {
                                  newValue.push(option);
                                }
                                handleSelectedSite(newValue);
                              }
                            }}
                          >
                            {option}
                          </Box>
                        </Box>
                        {(option === SELECT_ALL || option === DESELECT_ALL) && (
                          <Divider sx={{ mt: 0.5, mb: 0.5, width: "100%" }} />
                        )}
                      </>
                    ))}
                  </Box>
                )}
              </Box>
            </>
          )}
        </Box>
      </Box>
    </BaseConfigModal>
  );
}

export default withTranslation()(UserModal);